/**
 * Spec 101 — Per-candidate similarity preview panel.
 *
 * Mounted in `RuleCandidateDetailPage` between the supporting-quote panel
 * and the approve form. Fetches `GET /api/v1/admin/rules/candidates/:id/nearest-rules`
 * on mount and renders one of four states:
 *
 *   - loading: short one-liner while the fetch is in flight
 *   - error:   muted "Could not load nearest rules" — never blocks the page
 *   - empty:   "No embedded rules to compare against" + backfill link
 *   - populated: top-3 rows with slug + name + cosine similarity (3 decimals)
 *
 * The top row carries a "would move at ≥ X.XX" badge ONLY when its similarity
 * is below 0.85 (the default rescan threshold). The badge value rounds DOWN
 * to the nearest 0.05 so the number matches the modal's threshold input step
 * exactly — operator copy-paste UX, no rounding-up surprises.
 *
 * data-testid wiring for gate 101.A.5:
 *   - nearest-rules-loading
 *   - nearest-rules-error
 *   - nearest-rules-empty
 *   - nearest-rules-panel
 *   - nearest-rules-row     (one per match)
 *   - nearest-rules-badge   (top row only, conditional)
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { getNearestRules, type NearestRuleMatch } from '../api/ruleIngestion.js';

interface Props {
  /** Candidate UUID — matched against `/admin/rules/candidates/:id`. */
  id: string;
}

/**
 * Mirror of the backend's default rescan threshold (also 0.85 in dedup.ts).
 * Keep this constant in lockstep with `DEFAULT_THRESHOLD` in
 * `RescanCandidatesButton.tsx` so the badge's "would move" claim matches
 * the modal's pre-fill exactly.
 */
const RESCAN_DEFAULT_THRESHOLD = 0.85;

type State =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'error' }
  | { kind: 'populated'; matches: NearestRuleMatch[] };

export function NearestRulesPanel({ id }: Props) {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    getNearestRules(id)
      .then((res) => {
        if (cancelled) return;
        setState(
          res.matches.length === 0
            ? { kind: 'empty' }
            : { kind: 'populated', matches: res.matches },
        );
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state.kind === 'loading') {
    return (
      <div
        data-testid="nearest-rules-loading"
        className="text-sm text-slate-500"
      >
        Loading nearest rules…
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div
        data-testid="nearest-rules-error"
        className="text-sm text-slate-500"
      >
        Could not load nearest rules
      </div>
    );
  }

  if (state.kind === 'empty') {
    return (
      <div
        data-testid="nearest-rules-empty"
        className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600"
      >
        <p className="text-sm font-semibold text-slate-700">
          Nearest existing rules (embedding similarity)
        </p>
        <p className="mt-2">No embedded rules to compare against.</p>
        <Link
          to="/admin/rules"
          className="mt-2 inline-block text-violet-600 hover:underline"
        >
          Backfill embeddings →
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid="nearest-rules-panel"
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <p className="text-sm font-semibold text-slate-700">
        Nearest existing rules (embedding similarity)
      </p>
      <ul className="mt-3 space-y-2">
        {state.matches.map((m, idx) => {
          const sim = m.similarity;
          // Only the top match would move on rescan; the algorithm picks the
          // single nearest per candidate. Badging rows 2/3 would mislead.
          const showBadge = idx === 0 && sim < RESCAN_DEFAULT_THRESHOLD;
          // Floor to nearest 0.05 so the badge value lines up with the
          // modal's number input step. "would move at ≥ 0.793" is unactionable;
          // "≥ 0.75" tells the operator exactly what to type.
          const badgeThreshold = (Math.floor(sim * 20) / 20).toFixed(2);
          return (
            <li
              key={m.rule_id}
              data-testid="nearest-rules-row"
              className="flex items-baseline gap-3 text-sm"
            >
              <Link
                to={`/admin/rules?slug=${encodeURIComponent(m.slug)}`}
                className="font-mono text-violet-600 hover:underline"
              >
                {m.slug}
              </Link>
              <span className="text-slate-700">{m.name}</span>
              <span className="ml-auto font-mono font-semibold text-slate-900">
                {sim.toFixed(3)}
              </span>
              {showBadge && (
                <span
                  data-testid="nearest-rules-badge"
                  className="rounded-md bg-amber-100 px-2 py-0.5 text-xs text-amber-800"
                >
                  would move at ≥ {badgeThreshold}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
